import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, ListFilter } from 'lucide-react';
import { apiGet } from '../../lib/apiClient';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const monthKey = (year, month) => `${year}-${String(month + 1).padStart(2, '0')}`;
const parseMonthKey = (key) => {
  const [year, month] = String(key || '').split('-').map(Number);
  if (!year || !month) return null;
  return { year, month: month - 1, key };
};

export default function DailyPerformanceCalendar({ accountId = 'all', typeFilter = 'all' }) {
  const [viewMode, setViewMode] = useState('daily');
  const [rangeMode, setRangeMode] = useState('recent');
  const [selectedKey, setSelectedKey] = useState('');
  const [browserOpen, setBrowserOpen] = useState(false);
  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    const aqa = accountId !== 'all' ? `&account=${accountId}` : (typeFilter !== 'all' ? `&type_filter=${typeFilter}` : '');
    apiGet(`/api/reports/performance?period=all${aqa}`)
      .then(setPerformance)
      .catch(() => setPerformance(null));
  }, [accountId, typeFilter]);

  const dailyMap = useMemo(() => {
    const map = {};
    const rows = performance?.daily_data;
    if (Array.isArray(rows)) {
      rows.forEach((d) => {
        if (d && typeof d.date === 'string') map[d.date] = Number(d.profit) || 0;
      });
    }
    return map;
  }, [performance]);

  const tradingMonths = useMemo(() => {
    const set = new Set();
    Object.keys(dailyMap).forEach((dateStr) => {
      const parts = dateStr.split('-');
      if (parts.length >= 2) set.add(`${parts[0]}-${parts[1]}`);
    });
    const now = new Date();
    set.add(monthKey(now.getFullYear(), now.getMonth()));
    return Array.from(set)
      .sort()
      .map(parseMonthKey)
      .filter(Boolean);
  }, [dailyMap]);

  useEffect(() => {
    if (!selectedKey && tradingMonths.length > 0) {
      setSelectedKey(tradingMonths[tradingMonths.length - 1].key);
    }
  }, [selectedKey, tradingMonths]);

  const monthSummaries = useMemo(() => {
    return tradingMonths.map((m) => {
      const daysInMonth = new Date(m.year, m.month + 1, 0).getDate();
      let profit = 0;
      let activeDays = 0;
      let winningDays = 0;
      let losingDays = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `${m.key}-${String(d).padStart(2, '0')}`;
        const value = dailyMap[key];
        if (value !== undefined && value !== 0) {
          profit += value;
          activeDays++;
          if (value > 0) winningDays++;
          if (value < 0) losingDays++;
        }
      }
      return { ...m, profit, activeDays, winningDays, losingDays };
    });
  }, [dailyMap, tradingMonths]);

  const years = useMemo(() => Array.from(new Set(tradingMonths.map((m) => m.year))).sort((a, b) => b - a), [tradingMonths]);
  const selectedIndex = tradingMonths.findIndex((m) => m.key === selectedKey);
  const selected = tradingMonths[selectedIndex] || tradingMonths[tradingMonths.length - 1];

  const visibleMonths = useMemo(() => {
    if (rangeMode === 'all') return [...tradingMonths].reverse();
    if (rangeMode === 'year' && selected) return tradingMonths.filter((m) => m.year === selected.year).reverse();
    if (rangeMode === 'single' && selected) return [selected];
    return tradingMonths.slice(-2).reverse();
  }, [rangeMode, selected, tradingMonths]);

  const totals = useMemo(() => visibleMonths.reduce((acc, m) => {
    const row = monthSummaries.find((s) => s.key === m.key);
    if (!row) return acc;
    acc.profit += row.profit;
    acc.activeDays += row.activeDays;
    acc.winningDays += row.winningDays;
    acc.losingDays += row.losingDays;
    return acc;
  }, { profit: 0, activeDays: 0, winningDays: 0, losingDays: 0 }), [visibleMonths, monthSummaries]);

  const goMonth = (direction) => {
    if (selectedIndex < 0) return;
    const next = tradingMonths[selectedIndex + direction];
    if (!next) return;
    setSelectedKey(next.key);
    setRangeMode('single');
  };

  const handleYearChange = (year) => {
    const newestInYear = [...tradingMonths].reverse().find((m) => m.year === Number(year));
    if (newestInYear) {
      setSelectedKey(newestInYear.key);
      setRangeMode('year');
    }
  };

  const handleMonthChange = (key) => {
    setSelectedKey(key);
    setRangeMode('single');
  };

  if (tradingMonths.length === 0) {
    return <p className="text-[var(--text-dim)] text-sm">No daily performance data yet.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1">
          <SummaryPill label="Visible P/L" value={`${totals.profit >= 0 ? '+' : '-'}$${fmtMoney(totals.profit)}`} color={totals.profit >= 0 ? '#00ff88' : '#ff4757'} />
          <SummaryPill label="Active Days" value={totals.activeDays} color="var(--text-primary, #e6edf3)" />
          <SummaryPill label="Winning Days" value={totals.winningDays} color="#00ff88" />
          <SummaryPill label="Losing Days" value={totals.losingDays} color="#ff4757" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Segmented value={viewMode} onChange={setViewMode} options={[['daily', 'Daily'], ['weekly', 'Weekly']]} />
          <button onClick={() => { setRangeMode('recent'); setBrowserOpen(false); }} className={modeButton(rangeMode === 'recent')}>Recent 2</button>
          <button onClick={() => setRangeMode('year')} className={modeButton(rangeMode === 'year')}>Year</button>
          <button onClick={() => setRangeMode('all')} className={modeButton(rangeMode === 'all')}>All Months</button>
          <button onClick={() => setBrowserOpen((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border-color)] bg-[var(--bg-main)] text-xs font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
            <ListFilter size={13} /> Browse
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-main)] p-3">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays size={16} className="text-[#378ADD] shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[var(--text-primary)] truncate">{rangeLabel(rangeMode, selected, visibleMonths)}</p>
            <p className="text-[10px] text-[var(--text-dim)]">{tradingMonths.length} months available from synced history</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => goMonth(-1)} disabled={selectedIndex <= 0} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft size={14} /></button>
          <select value={selected?.year || ''} onChange={(e) => handleYearChange(e.target.value)} className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-md px-3 py-1.5 text-xs text-[var(--text-primary)]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={selected?.key || ''} onChange={(e) => handleMonthChange(e.target.value)} className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-md px-3 py-1.5 text-xs text-[var(--text-primary)] min-w-[145px]">
            {[...tradingMonths].reverse().map((m) => <option key={m.key} value={m.key}>{MONTH_NAMES[m.month]} {m.year}</option>)}
          </select>
          <button onClick={() => goMonth(1)} disabled={selectedIndex >= tradingMonths.length - 1} className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed"><ChevronRight size={14} /></button>
        </div>
      </div>

      {browserOpen && (
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-main)] p-3">
          <div className="flex flex-wrap gap-2">
            {[...monthSummaries].reverse().map((m) => (
              <button
                key={m.key}
                onClick={() => { setSelectedKey(m.key); setRangeMode('single'); }}
                className="px-3 py-2 rounded-lg border text-left min-w-[128px] transition"
                style={{
                  borderColor: selectedKey === m.key && rangeMode === 'single' ? '#378ADD' : 'var(--border-color, #21262d)',
                  background: selectedKey === m.key && rangeMode === 'single' ? 'rgba(55,138,221,0.12)' : 'var(--bg-card, #161b22)',
                }}
              >
                <div className="text-[11px] font-semibold text-[var(--text-primary)]">{MONTH_NAMES[m.month].slice(0, 3)} {m.year}</div>
                <div className="text-[12px] font-bold" style={{ color: m.profit >= 0 ? '#00ff88' : '#ff4757' }}>{m.profit >= 0 ? '+' : '-'}${fmtMoney(m.profit)}</div>
                <div className="text-[9px] text-[var(--text-dim)]">{m.activeDays} active days</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {visibleMonths.map(({ year, month }) => (
          viewMode === 'daily'
            ? <MonthDaily key={`daily-${year}-${month}`} year={year} month={month} dailyMap={dailyMap} />
            : <MonthWeekly key={`weekly-${year}-${month}`} year={year} month={month} dailyMap={dailyMap} />
        ))}
      </div>
    </div>
  );
}

function SummaryPill({ label, value, color }) {
  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-main)] p-3">
      <p className="text-[9px] uppercase tracking-wide text-[var(--text-secondary)] mb-1">{label}</p>
      <p className="text-sm font-bold font-mono" style={{ color }}>{value}</p>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="flex rounded-md overflow-hidden border border-[var(--border-color)] bg-[var(--bg-main)]">
      {options.map(([key, label]) => (
        <button key={key} onClick={() => onChange(key)} className={`px-3 py-1.5 text-xs font-semibold ${value === key ? 'bg-[#378ADD] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function modeButton(active) {
  return `px-3 py-1.5 rounded-md text-xs font-semibold border transition ${active ? 'bg-[#378ADD] text-white border-[#378ADD]' : 'bg-[var(--bg-main)] border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`;
}

function rangeLabel(mode, selected, visibleMonths) {
  if (!selected) return 'No month selected';
  if (mode === 'all') return 'All available months';
  if (mode === 'year') return `${selected.year} calendar year`;
  if (mode === 'single') return `${MONTH_NAMES[selected.month]} ${selected.year}`;
  if (visibleMonths.length >= 2) {
    const newest = visibleMonths[0];
    const previous = visibleMonths[1];
    return `${MONTH_NAMES[previous.month]} ${previous.year} and ${MONTH_NAMES[newest.month]} ${newest.year}`;
  }
  return `${MONTH_NAMES[selected.month]} ${selected.year}`;
}

function MonthHeader({ year, month, monthTotal, tradeDays, expanded, onToggle }) {
  return (
    <div onClick={onToggle} className="flex items-center justify-between px-4 py-3.5 cursor-pointer gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[#378ADD] text-xs w-3">{expanded ? 'v' : '>'}</span>
          <span className="text-gray-100 text-sm font-bold truncate">{MONTH_NAMES[month]} {year}</span>
        </div>
        <p className="text-[10px] text-[var(--text-dim)] ml-5">{tradeDays} active day{tradeDays === 1 ? '' : 's'}</p>
      </div>
      <div className="text-right shrink-0">
        <span className="text-sm font-bold" style={{ color: monthTotal >= 0 ? '#00ff88' : '#ff4757' }}>
          {monthTotal >= 0 ? '+' : '-'}${fmtMoney(monthTotal)}
        </span>
      </div>
    </div>
  );
}

function SummaryGrid({ monthTotal, tradeDays, winningDays, losingDays }) {
  const suggested = monthTotal > 0 ? Math.round(monthTotal * 0.3) : 0;
  return (
    <div className="pb-4 mb-4 border-b border-white/[0.08]">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <MiniBox label="Trading Profit" value={`${monthTotal >= 0 ? '+' : '-'}$${fmtMoney(monthTotal)}`} color={monthTotal >= 0 ? '#00ff88' : '#ff4757'} />
        <MiniBox label="Active Days" value={tradeDays} color="var(--text-primary, #e6edf3)" />
        <MiniBox label="Win / Loss Days" value={`${winningDays} / ${losingDays}`} color="#22d3ee" />
        <MiniBox label="Suggested 30%" value={`$${suggested.toLocaleString()}`} color="#a855f7" />
      </div>
    </div>
  );
}

function MiniBox({ label, value, color }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg p-3 text-center">
      <div className="text-[10px] text-gray-500 uppercase mb-1">{label}</div>
      <div className="text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function getMonthStats(year, month, dailyMap) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let monthTotal = 0, tradeDays = 0, winningDays = 0, losingDays = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const value = dailyMap[key];
    if (value !== undefined && value !== 0) {
      monthTotal += value;
      tradeDays++;
      if (value > 0) winningDays++;
      if (value < 0) losingDays++;
    }
  }
  return { daysInMonth, monthTotal, tradeDays, winningDays, losingDays };
}

function dayStyle(profit, hasTrade) {
  if (!hasTrade) return { bg: '#161b22', border: '1px solid transparent' };
  const abs = Math.abs(profit);
  if (profit > 0) {
    if (abs > 2000) return { bg: 'rgba(0,255,136,0.25)', border: '1px solid rgba(0,255,136,0.42)' };
    if (abs > 500) return { bg: 'rgba(0,255,136,0.18)', border: '1px solid rgba(0,255,136,0.3)' };
    return { bg: 'rgba(0,255,136,0.12)', border: '1px solid rgba(0,255,136,0.2)' };
  }
  if (abs > 2000) return { bg: 'rgba(255,71,87,0.3)', border: '1px solid rgba(255,71,87,0.5)' };
  if (abs > 500) return { bg: 'rgba(255,71,87,0.2)', border: '1px solid rgba(255,71,87,0.35)' };
  return { bg: 'rgba(255,71,87,0.12)', border: '1px solid rgba(255,71,87,0.2)' };
}

function MonthDaily({ year, month, dailyMap }) {
  const [expanded, setExpanded] = useState(false);
  const firstDay = new Date(year, month, 1).getDay();
  const { daysInMonth, monthTotal, tradeDays, winningDays, losingDays } = getMonthStats(year, month, dailyMap);

  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-xl overflow-hidden">
      <MonthHeader year={year} month={month} monthTotal={monthTotal} tradeDays={tradeDays} expanded={expanded} onToggle={() => setExpanded(!expanded)} />
      {expanded && <div className="px-4"><SummaryGrid monthTotal={monthTotal} tradeDays={tradeDays} winningDays={winningDays} losingDays={losingDays} /></div>}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-7 gap-[3px] mb-[3px]">
          {WEEKDAYS.map((d, i) => <div key={d} className="text-center text-[10px] font-semibold py-1" style={{ color: i === 0 || i === 6 ? '#ff4757' : '#8b949e' }}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-[3px]">
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} className="aspect-square min-h-[36px]" />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const profit = dailyMap[key];
            const hasTrade = profit !== undefined && profit !== 0;
            const s = dayStyle(profit || 0, hasTrade);
            return (
              <div key={day} title={hasTrade ? `${MONTH_NAMES[month]} ${day}: ${profit >= 0 ? '+' : '-'}$${fmtMoney(profit)}` : `${MONTH_NAMES[month]} ${day}: no trades`} className="aspect-square min-h-[36px] rounded-md flex flex-col items-center justify-center" style={{ background: s.bg, border: s.border }}>
                <span className="text-[10px] text-gray-500 leading-none">{day}</span>
                {hasTrade && <span className="text-[9px] font-bold leading-none mt-0.5" style={{ color: profit >= 0 ? '#00ff88' : '#ff4757' }}>{profit >= 0 ? '+' : '-'}{Math.abs(profit) >= 10000 ? `$${(Math.abs(profit) / 1000).toFixed(0)}k` : `$${fmtMoney(profit)}`}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MonthWeekly({ year, month, dailyMap }) {
  const [expanded, setExpanded] = useState(false);
  const monthShort = MONTH_NAMES[month].slice(0, 3);
  const { daysInMonth, monthTotal, tradeDays, winningDays, losingDays } = getMonthStats(year, month, dailyMap);
  const weeks = [];
  let weekStart = 1;
  while (weekStart <= daysInMonth) {
    const dayOfWeek = new Date(year, month, weekStart).getDay();
    const weekEnd = Math.min(weekStart + (6 - dayOfWeek), daysInMonth);
    let profit = 0, active = 0;
    for (let d = weekStart; d <= weekEnd; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const value = dailyMap[key];
      if (value !== undefined && value !== 0) { profit += value; active++; }
    }
    weeks.push({ num: weeks.length + 1, startDay: weekStart, endDay: weekEnd, profit, active });
    weekStart = weekEnd + 1;
  }

  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-xl overflow-hidden">
      <MonthHeader year={year} month={month} monthTotal={monthTotal} tradeDays={tradeDays} expanded={expanded} onToggle={() => setExpanded(!expanded)} />
      {expanded && <div className="px-4"><SummaryGrid monthTotal={monthTotal} tradeDays={tradeDays} winningDays={winningDays} losingDays={losingDays} /></div>}
      <div className="px-4 pb-4">
        <div className="flex flex-col gap-2">
          {weeks.map((w) => {
            const hasTrade = w.profit !== 0;
            return (
              <div key={w.num} className="flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: hasTrade ? (w.profit > 0 ? 'rgba(0,255,136,0.06)' : 'rgba(255,71,87,0.06)') : '#161b22', border: hasTrade ? (w.profit > 0 ? '1px solid rgba(0,255,136,0.15)' : '1px solid rgba(255,71,87,0.15)') : '1px solid #21262d' }}>
                <div>
                  <div className="text-[13px] font-bold text-gray-100">Week {w.num}</div>
                  <div className="text-[10px] text-gray-600">{monthShort} {w.startDay} - {w.endDay}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: w.profit >= 0 ? '#00ff88' : '#ff4757' }}>{hasTrade ? `${w.profit > 0 ? '+' : '-'}$${fmtMoney(w.profit)}` : '-'}</div>
                  <div className="text-[10px] text-gray-600">{w.active} active days</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}